import type {
  OrderDiscountItemModel,
  OrderReceiptSubTotalsModel,
} from './OrderReceipt.api.model';
import type { OrderReceiptItemModel } from './OrderReceiptItem.api.model';

export const YOU_HAVE_SAVED_LABEL = 'Du har sparat';
export const INSURANCE_LABEL = 'Försäkring';
export const OBTAINED_DISCOUNT_LABEL = 'Erhållen rabatt';
export const CAMPAIGN_LABEL = 'Kampanj';
export const DISCOUNT_LABEL = 'Rabatt';
export const NO_PRICE_LABEL = 'Inget pris';
export const ZERO_KR_TO_PAY_NOW_LABEL = '0 kr att betala nu';
export const INSURANCE_LOADING_ROW_ID = 'insurance_loading_row';

export type SummaryItem = {
  id: string;
  imgAltText?: string;
  imgUrl?: string;
  priceMainLabel: string;
  priceSubLabel?: string;
  showLabelsReversedOrder: boolean;
  subTitle?: string;
  title: string;
  isLoadingUi?: boolean;
};

export const createHardWareSummaryItem = (
  item: OrderReceiptItemModel,
): SummaryItem => {
  const mainPriceString =
    item.SelectedPayment?.ProductDownPayment.toLocaleString('sv-SE') !==
      undefined &&
    item.SelectedPayment?.ProductDownPayment.toLocaleString('sv-SE') !== null &&
    item.SelectedPayment.ProductPaymentUnit
      ? `${item.SelectedPayment.ProductDownPayment.toLocaleString('sv-SE')} ${item.SelectedPayment.ProductPaymentUnit}`
      : NO_PRICE_LABEL;

  const color = `${item?.ColorName ?? ''}`;
  const size = `${item.StorageSize?.DataSize ?? ''} ${item.StorageSize?.DataUnit ?? ''}`;
  const subTitle = `${color} ${size}`;

  // If the item was actually bought with direct payment then
  // the price in the main string is clear enough
  let directPaymentString = '';
  if (item.SelectedPayment?.DownPayments !== 1) {
    directPaymentString = ZERO_KR_TO_PAY_NOW_LABEL;
  }

  const phoneName = `${item.ManufacturerName ?? ''} ${item.ProductName ?? ''}`;
  const titleString = `${phoneName}`;

  return {
    id: item.Id.toString(),
    imgUrl: item.Picture?.ImageUrl,
    imgAltText: '',
    priceMainLabel: mainPriceString,
    priceSubLabel: directPaymentString,
    showLabelsReversedOrder: false,
    subTitle: subTitle,
    title: titleString,
  };
};

export const createBuyBackSummaryItem = (
  item: OrderReceiptItemModel,
): SummaryItem => {
  let mainPriceString = '';
  if (item?.DataAllowance?.DataSize && item?.DataAllowance?.DataUnit) {
    mainPriceString = `+${item.DataAllowance.DataSize} ${item.DataAllowance.DataUnit}/mån`;
  } else if (item?.MonthlyPrice) {
    mainPriceString = `-${item.MonthlyPrice.toLocaleString('sv-SE')} kr/mån`;
  } else if (item?.OneTimePrice) {
    mainPriceString = `-${item.OneTimePrice.toLocaleString('sv-SE')} kr`;
  }

  const color = `${item?.ColorName ?? ''}`;
  const condition = `${item?.Condition ?? ''}`;
  const size = `${item.StorageSize?.DataSize ?? ''} ${item.StorageSize?.DataUnit ?? ''}`;
  const phoneName = `${item.ManufacturerName ?? ''} ${item.ProductName ?? ''}`;
  const subTitle = `${phoneName} ${color} ${condition} ${size}`;

  const titleString = 'Din inbytestelefon';

  return {
    id: item.Id.toString(),
    imgUrl: '/shop/images/checkout/Buyback/buyback-phone.svg',
    imgAltText: '',
    priceMainLabel: 'Inbytesrabatt: ',
    priceSubLabel: mainPriceString,
    showLabelsReversedOrder: false,
    subTitle: subTitle,
    title: titleString,
  };
};

export const createBundledSubscriptionSummaryItem = (
  item: OrderReceiptItemModel,
  appliedDiscounts: OrderDiscountItemModel[],
  isFamilyPlan: boolean = false,
): SummaryItem => {
  const discountValidityInMonths = appliedDiscounts.filter(
    (appliedDiscounts) =>
      appliedDiscounts.OrderItemId === item.Id &&
      appliedDiscounts.ValidityInMonths,
  )[0]?.ValidityInMonths;

  const priceMainLabel =
    item?.PriceText ??
    order_makeMainPriceStringForSubscriptionItem(
      item?.MonthlyPrice,
      item?.RegularMonthlyPrice,
      discountValidityInMonths,
    );
  const bindingPeriodString = order_makeBindingPeriodStringForSubscriptionItem(
    item?.BindingPeriod,
  );

  const titleString = `${item?.ProductName} ${bindingPeriodString}`;
  const imgUrl = images_selectImageUrlForSubscriptionItemInList(
    undefined,
    undefined,
    item.ProductName,
    isFamilyPlan,
  );

  return {
    id: item?.Id.toString() ?? '',
    imgUrl,
    imgAltText: '',
    priceMainLabel: priceMainLabel,
    priceSubLabel: ZERO_KR_TO_PAY_NOW_LABEL,
    showLabelsReversedOrder: false,
    subTitle: undefined,
    title: titleString,
  };
};

export const createStandalonePrePaidSummaryItem = (
  item: OrderReceiptItemModel,
): SummaryItem => {
  const priceMainLabel = order_makeMainPriceStringForPrePaidItem(
    item?.OneTimePrice,
  );

  const bindingPeriodString = order_makeBindingPeriodStringForSubscriptionItem(
    item?.BindingPeriod,
  );

  const titleString = `${item.ProductName} ${bindingPeriodString}`;
  const imgUrl = images_makeImageUrlStringForPrePaidItem(
    item.ProductName,
    item.OneTimePrice,
  );

  return {
    id: `${item.Id ?? ''}`,
    imgUrl,
    imgAltText: '',
    priceMainLabel,
    priceSubLabel: '',
    showLabelsReversedOrder: false,
    subTitle: undefined,
    title: titleString,
  };
};

export const createStandaloneSubscriptionSummaryItem = (
  item: OrderReceiptItemModel,
  appliedDiscounts: OrderDiscountItemModel[],
  isFamilyPlan: boolean = false,
  isMobileBrodband: boolean = false,
): SummaryItem => {
  const discountValidityInMonths = appliedDiscounts.filter(
    (appliedDiscounts) =>
      appliedDiscounts.OrderItemId === item.Id &&
      appliedDiscounts.ValidityInMonths,
  )[0]?.ValidityInMonths;

  const priceMainLabel = order_makeMainPriceStringForSubscriptionItem(
    item?.MonthlyPrice,
    item?.RegularMonthlyPrice,
    discountValidityInMonths,
  );

  const bindingPeriodString = order_makeBindingPeriodStringForSubscriptionItem(
    item?.BindingPeriod,
  );

  const titleString = `${item.ProductName} ${bindingPeriodString}`;
  const imgUrl = images_selectImageUrlForSubscriptionItemInList(
    undefined,
    undefined,
    item.ProductName,
    isFamilyPlan,
    isMobileBrodband,
  );

  return {
    id: item.Id.toString() ?? '',
    imgUrl,
    imgAltText: '',
    priceMainLabel,
    priceSubLabel: '',
    showLabelsReversedOrder: false,
    subTitle: undefined,
    title: titleString,
  };
};

export const createSavingsSummaryItem = (
  subTotals: OrderReceiptSubTotalsModel,
): SummaryItem => {
  return {
    id: `${subTotals.SubTotalSavings}`,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel: `${subTotals.SubTotalSavings} kr`,
    priceSubLabel: OBTAINED_DISCOUNT_LABEL,
    showLabelsReversedOrder: true,
    subTitle: undefined,
    title: YOU_HAVE_SAVED_LABEL,
  };
};

export const createInsuranceItem = (
  item: OrderReceiptItemModel,
): SummaryItem => {
  const id = item.Id.toString();

  const title = item.ProductName;
  const subTitle = '';

  const priceMainLabel =
    `${item.MonthlyPrice === 0 ? item.MonthlyPrice + ' kr/mån' : item.MonthlyPrice}` ||
    '';
  const priceSubLabel = item.PriceText;

  return {
    id,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel,
    priceSubLabel,
    showLabelsReversedOrder: false,
    subTitle,
    title,
  };
};

export const createDiscountCampaignSummaryItem = (
  discount: OrderDiscountItemModel,
): SummaryItem => {
  const {
    Name,
    OrderItemId,
    DiscountAmount,
    DiscountAmountUnit,
    DiscountPercentage,
    DataAllowance,
    ValidityInMonths,
    DiscountType,
  } = discount;

  const id = `${Name}${OrderItemId}`;
  const isFreeProduct = DiscountType === 'FreeProduct';
  const isDataAllowanceCampaign =
    DataAllowance && DataAllowance > 0 && DiscountAmount === 0;
  const showDiscountPercentage = DiscountAmount === 0 && DiscountPercentage > 0;

  let priceMainLabel = showDiscountPercentage
    ? `${DiscountPercentage}%`
    : `${DiscountAmount} ${DiscountAmountUnit}`;

  const priceSubLabel = [
    DataAllowance ? `${DataAllowance} GB` : '',
    ValidityInMonths ? `i ${ValidityInMonths} månader` : '',
  ]
    .filter(Boolean)
    .join(' ');

  // If it's a free product or data allowance campaign, don't show any price
  if (isFreeProduct || isDataAllowanceCampaign) {
    priceMainLabel = '';
  }

  return {
    id,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel,
    priceSubLabel,
    showLabelsReversedOrder: priceMainLabel === '',
    subTitle: '',
    title: Name,
  };
};

export const createInsuranceLoadingContentRow = (): SummaryItem => {
  const id = INSURANCE_LOADING_ROW_ID;

  return {
    id,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel: '',
    priceSubLabel: '',
    showLabelsReversedOrder: false,
    subTitle: '',
    title: '',
    isLoadingUi: true,
  };
};

export const createAddOnItem = (item: OrderReceiptItemModel): SummaryItem => {
  const id = item.Id.toString();

  const title = item.ProductName;
  const subTitle = '';

  const priceMainLabel =
    `${item.MonthlyPrice !== 0 ? item.MonthlyPrice + ' kr/mån' : item.MonthlyPrice}` ||
    '';
  const priceSubLabel = item.PriceText;

  return {
    id,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel,
    priceSubLabel,
    showLabelsReversedOrder: false,
    subTitle,
    title,
  };
};

export const createExtraSimItem = (
  item: OrderReceiptItemModel,
): SummaryItem => {
  const id = item.Id.toString();

  const title = item.ProductName;
  const subTitle = '';

  const priceMainLabel =
    `${item.MonthlyPrice !== 0 ? item.MonthlyPrice + ' kr/mån' : item.MonthlyPrice}` ||
    '';
  const priceSubLabel = item.PriceText;

  return {
    id,
    imgUrl: '',
    imgAltText: '',
    priceMainLabel,
    priceSubLabel,
    showLabelsReversedOrder: false,
    subTitle,
    title,
  };
};
